import { useEffect, useMemo, useRef, useState } from 'react'
import { COLORS } from '../lib/theme'
import { Icon } from './Icon'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

/** Approximate rendered height of the calendar popover in px. */
const POPOVER_HEIGHT = 310
const POPOVER_WIDTH  = 280
const GAP = 6

type CalendarDay =
  | null
  | { iso: string; date: number; isToday: boolean; isSelected: boolean; isDisabled: boolean }

function buildDays(
  year: number,
  month: number,
  selected: string,
  min: string,
  today: string,
): CalendarDay[] {
  const firstDow = new Date(year, month, 1).getDay() // 0=Sun
  const startOffset = (firstDow + 6) % 7             // shift to Mon-first
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const days: CalendarDay[] = []

  for (let i = 0; i < startOffset; i++) days.push(null)

  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    days.push({
      iso,
      date: d,
      isToday: iso === today,
      isSelected: iso === selected,
      isDisabled: min ? iso < min : false,
    })
  }
  return days
}

function formatDisplay(iso: string): string {
  // new Date('YYYY-MM-DD') parses as UTC midnight; add noon to avoid DST shifts
  const d = new Date(`${iso}T12:00:00`)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Compute `position:fixed` coordinates for the popover from the trigger rect. */
function resolvePopoverPos(rect: DOMRect, height: number, width: number): React.CSSProperties {
  const spaceBelow = window.innerHeight - rect.bottom
  const left = Math.min(rect.left, window.innerWidth - width - 8)

  if (spaceBelow >= height + GAP) {
    return { position: 'fixed', top: rect.bottom + GAP, left }
  }
  // Not enough room below — open above the trigger
  return { position: 'fixed', bottom: window.innerHeight - rect.top + GAP, left }
}

export interface WaDatePickerProps {
  value: string        // YYYY-MM-DD or ''
  min?: string         // YYYY-MM-DD — dates before this are disabled
  onChange: (v: string) => void
}

export function WaDatePicker({ value, min, onChange }: WaDatePickerProps) {
  const today = new Date().toISOString().slice(0, 10)
  const [open, setOpen] = useState(false)
  const [popoverPos, setPopoverPos] = useState<React.CSSProperties>({})
  const triggerRef = useRef<HTMLButtonElement>(null)

  const [viewYear, setViewYear] = useState(() =>
    value ? parseInt(value.slice(0, 4)) : new Date().getFullYear(),
  )
  const [viewMonth, setViewMonth] = useState(() =>
    value ? parseInt(value.slice(5, 7)) - 1 : new Date().getMonth(),
  )

  // Keep view in sync when value changes from outside
  useEffect(() => {
    if (value) {
      setViewYear(parseInt(value.slice(0, 4)))
      setViewMonth(parseInt(value.slice(5, 7)) - 1)
    }
  }, [value])

  const days = useMemo(
    () => buildDays(viewYear, viewMonth, value, min ?? '', today),
    [viewYear, viewMonth, value, min, today],
  )

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      setPopoverPos(
        resolvePopoverPos(triggerRef.current.getBoundingClientRect(), POPOVER_HEIGHT, POPOVER_WIDTH),
      )
    }
    setOpen((v) => !v)
  }

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1) }
    else setViewMonth((m) => m - 1)
  }
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1) }
    else setViewMonth((m) => m + 1)
  }
  const select = (iso: string) => { onChange(iso); setOpen(false) }

  const displayValue = value ? formatDisplay(value) : ''

  return (
    <div style={{ position: 'relative' }}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        className="wh-input"
        onClick={handleToggle}
        aria-label="Pick date"
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          cursor: 'pointer', width: '100%', textAlign: 'left',
          color: displayValue ? COLORS.onSurface : COLORS.outline,
        }}
      >
        <span style={{ flex: 1, fontSize: 14 }}>
          {displayValue || 'Pick a date'}
        </span>
        <Icon name="calendar" size={16} color={COLORS.outline} />
      </button>

      {open && (
        <>
          {/* Click-outside overlay (fixed, full-screen) */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 1200 }}
            onClick={() => setOpen(false)}
          />

          {/* Calendar popover — position:fixed escapes any overflow:hidden ancestors */}
          <div
            role="dialog"
            aria-label="Date picker"
            style={{
              ...popoverPos,
              zIndex: 1201,
              background: COLORS.surfaceContainerLowest,
              borderRadius: 12,
              border: `1px solid ${COLORS.surfaceContainerHighest}`,
              boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
              padding: '12px 12px 14px',
              width: POPOVER_WIDTH,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Month / year header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <button
                type="button"
                onClick={prevMonth}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: 4, borderRadius: 6, display: 'flex', color: COLORS.onSurface,
                }}
                aria-label="Previous month"
              >
                <Icon name="chevron-left" size={18} />
              </button>
              <span style={{ flex: 1, textAlign: 'center', fontSize: 13, fontWeight: 600 }}>
                {MONTH_NAMES[viewMonth]} {viewYear}
              </span>
              <button
                type="button"
                onClick={nextMonth}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: 4, borderRadius: 6, display: 'flex', color: COLORS.onSurface,
                }}
                aria-label="Next month"
              >
                <Icon name="chevron-right" size={18} />
              </button>
            </div>

            {/* Weekday labels */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 2 }}>
              {WEEKDAYS.map((d) => (
                <div
                  key={d}
                  style={{
                    textAlign: 'center', fontSize: 11, fontWeight: 600,
                    color: COLORS.outline, padding: '3px 0',
                  }}
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Day grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
              {days.map((day, i) =>
                day === null ? (
                  <div key={i} />
                ) : (
                  <button
                    key={day.iso}
                    type="button"
                    disabled={day.isDisabled}
                    onClick={() => select(day.iso)}
                    style={{
                      width: 34, height: 34, margin: 'auto',
                      borderRadius: '50%',
                      border: day.isToday && !day.isSelected
                        ? `1.5px solid ${COLORS.primary}`
                        : '1.5px solid transparent',
                      background: day.isSelected ? COLORS.primary : 'transparent',
                      color: day.isSelected
                        ? COLORS.onPrimary
                        : day.isDisabled
                          ? COLORS.outlineVariant
                          : COLORS.onSurface,
                      fontSize: 12,
                      fontWeight: day.isSelected || day.isToday ? 700 : 400,
                      cursor: day.isDisabled ? 'default' : 'pointer',
                      opacity: day.isDisabled ? 0.4 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      outline: 'none',
                      transition: 'background 80ms',
                    }}
                  >
                    {day.date}
                  </button>
                ),
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
